import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import {
  addUserToNotify,
  createOrderItem,
  deleteOrderItemRequest,
  deleteUserToNotify,
  getHospitalityInfoFor,
  updateOrderItemRequest
} from "@features/meeting-rooms/pages/models/hospitality.http";
import { OfficeEntity, updateSingleOfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { HospitalityOrderItem } from "../../../../meeting-rooms/models/hospitality.models";
import { AppState } from '@app/store/state/app.state';
import { getOfficeChildren } from '@shared/utils/OfficeHelper';
import { setEditingOffice } from '@features/desks/store/editOfficeLayout.duck';
import { closeDialog } from "@shared/store/dialog.duck";
import { BasicUserInfo } from '@shared/models/user.models';
import { warningNotification } from "@shared/store/notification.duck";


export interface HospitalityManagementState {
  orderItems: HospitalityOrderItem[];
  usersToNotify: BasicUserInfo[];
  loading: boolean;
}

export const initialHospitalityManagementState: HospitalityManagementState = {
  orderItems: [],
  usersToNotify: [],
  loading: false,
}

export const enableHospitalityForOffice: any = createAsyncThunk(
  'hospitalityManagement/enableHospitalityForOffice',
  async (params: {office: OfficeEntity, enabledState: boolean}, thunkAPI) => {
    const response = await updateSingleOfficeEntity({
      ...params.office,
      hospitalityEnabled: params.enabledState,
    });

    const allOffices = (thunkAPI.getState() as AppState).advanceHotDeskingSetup.offices;
    const officeChildren = getOfficeChildren(params.office.id, allOffices);
    thunkAPI.dispatch(setEditingOffice({office: response, children: officeChildren}))
  }
)

export const updateImportantNoteVisibility: any = createAsyncThunk(
  'hospitalityManagement/updateImportantNoteVisibility',
  async (params: {office: OfficeEntity, enabledState: boolean}, thunkAPI) => {
    const response = await updateSingleOfficeEntity({
      ...params.office,
      hospitalImportantNoteVisible: params.enabledState,
    });

    const allOffices = (thunkAPI.getState() as AppState).advanceHotDeskingSetup.offices;
    const officeChildren = getOfficeChildren(params.office.id, allOffices);
    thunkAPI.dispatch(setEditingOffice({office: response, children: officeChildren}))
  }
)

export const updateImportantNote: any = createAsyncThunk(
  'hospitalityManagement/updateImportantNote',
  async (params: {office: OfficeEntity, note: string}, thunkAPI) => {
    const response = await updateSingleOfficeEntity({
      ...params.office,
      hospitalityImportantNote: params.note,
    });

    const allOffices = (thunkAPI.getState() as AppState).advanceHotDeskingSetup.offices;
    const officeChildren = getOfficeChildren(params.office.id, allOffices);
    thunkAPI.dispatch(setEditingOffice({office: response, children: officeChildren}))
  }
)

export const createNewOrderItem: any = createAsyncThunk(
  'hospitalityManagement/createNewOrderItem',
  async (params: {office: OfficeEntity, orderItem: HospitalityOrderItem}, thunkAPI) => {
    await createOrderItem(params.orderItem);
    thunkAPI.dispatch(loadHospitalityInfo({office: params.office}));
    thunkAPI.dispatch(closeDialog());
  }
)

export const updateOrderItem: any = createAsyncThunk(
  'hospitalityManagement/updateOrderItem',
  async (params: {office: OfficeEntity, orderItem: HospitalityOrderItem}, thunkAPI) => {
    await updateOrderItemRequest(params.orderItem);
    thunkAPI.dispatch(loadHospitalityInfo({office: params.office}));
    thunkAPI.dispatch(closeDialog());
  }
)

export const deleteOrderItem: any = createAsyncThunk(
  'hospitalityManagement/deleteOrderItem',
  async (params: {office: OfficeEntity, orderItem: HospitalityOrderItem}, thunkAPI) => {
    await deleteOrderItemRequest(params.orderItem);
    thunkAPI.dispatch(loadHospitalityInfo({office: params.office}));
    thunkAPI.dispatch(closeDialog());
  }
)

export const updateUsersToNotify: any = createAsyncThunk(
  'hospitalityManagement/updateUsersToNotify',
  async (params: {office: OfficeEntity, userToNotify: BasicUserInfo}, thunkAPI) => {
    const appState = (thunkAPI.getState() as AppState);
    let usersToNotify = [...appState.hospitalityManagement.usersToNotify];
    const idx = usersToNotify.findIndex((u: BasicUserInfo) => u.userId === params.userToNotify.userId);
    if (idx < 0) {
      try {
        await addUserToNotify(params.userToNotify, params.office);
        usersToNotify.push(params.userToNotify);
      } catch (e) {
        thunkAPI.dispatch(warningNotification('Failed to add user'));
      }
    } else {
      await deleteUserToNotify(params.userToNotify, params.office);
      usersToNotify = usersToNotify.splice(idx, 1);
    }
    return usersToNotify;
  }
)

export const loadHospitalityInfo: any = createAsyncThunk(
  'hospitalityManagement/loadHospitalityInfo',
  async (params: {office: OfficeEntity}, thunkAPI) => {
    const response = await getHospitalityInfoFor(params.office)
    return {
      orderItems: response.orderItems,
      usersToNotify: response.usersToNotify,
    }
  }
)

const hospitalityManagement = createSlice({
  name: 'hospitalityManagement',
  initialState: initialHospitalityManagementState,
  reducers: {
    reset: () => initialHospitalityManagementState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadHospitalityInfo.pending, (state, action) => ({...state, loading: true,}))
      .addCase(loadHospitalityInfo.rejected, (state, action) => ({...state, loading: false,}))
      .addCase(loadHospitalityInfo.fulfilled, (state, action) => ({
      ...state,
      orderItems: action.payload.orderItems,
      usersToNotify: action.payload.usersToNotify,
      loading: false,
    }))
      .addCase(enableHospitalityForOffice.pending, (state, action) => ({...state, loading: true,}))
      .addCase(enableHospitalityForOffice.rejected, (state, action) => ({...state, loading: false,}))
      .addCase(enableHospitalityForOffice.fulfilled, (state, action) => ({...state, loading: false,}))
      .addCase(createNewOrderItem.pending, (state, action) => ({...state, loading: true,}))
      .addCase(createNewOrderItem.rejected, (state, action) => ({...state, loading: false,}))
      .addCase(createNewOrderItem.fulfilled, (state, action) => ({...state, loading: false,}))
      .addCase(updateOrderItem.pending, (state, action) => ({...state, loading: true,}))
      .addCase(updateOrderItem.rejected, (state, action) => ({...state, loading: false,}))
      .addCase(updateOrderItem.fulfilled, (state, action) => ({...state, loading: false,}))
      .addCase(deleteOrderItem.pending, (state, action) => ({...state, loading: true,}))
      .addCase(deleteOrderItem.rejected, (state, action) => ({...state, loading: false,}))
      .addCase(deleteOrderItem.fulfilled, (state, action) => ({...state, loading: false,}))
      .addCase(updateUsersToNotify.pending, (state, action) => ({...state, loading: true,}))
      .addCase(updateUsersToNotify.rejected, (state, action) => ({...state, loading: false,}))
      .addCase(updateUsersToNotify.fulfilled, (state, action) => ({...state, loading: false, usersToNotify: action.payload}));
  }
})

export default hospitalityManagement.reducer;


// Selectors
export const selectAllOrderItems = (state: AppState) => state.hospitalityManagement.orderItems;
export const selectUsersToNotify = (state: AppState) => state.hospitalityManagement.usersToNotify;
export const selectIsLoading = (state: AppState) => state.hospitalityManagement.loading;
